import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Download, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";

export default function VendorAgreement() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const isVendorPreSignup = user && user !== false && user.role === "vendor" && !user.vendor_id;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="vendor-agreement-page">
      {!isVendorPreSignup && (
        <Link to="/vendor/register" className="inline-flex items-center gap-1 text-sm text-[#1E3A2B] font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to vendor registration
        </Link>
      )}

      <div className="mb-8">
        <span className="badge-sage inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Step 1 of 2 · Legal Acceptance</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Marketplace Vendor Agreement</h1>
        <p className="mt-2 text-[#5A6B5D] max-w-3xl">
          Welcome to Healthion India{user && user.name ? `, ${user.name}` : ""}. Before submitting your KYC documents, please review and accept the binding agreement between your business and <strong>Amiroop Healthion LLP</strong>.
        </p>
        <button
          onClick={() => window.print()}
          className="mt-4 btn-outline-forest text-sm inline-flex items-center gap-2"
          data-testid="agreement-print-button"
        >
          <Download className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <article className="bg-white border border-[#E2DDD5] rounded-3xl p-8 sm:p-10 space-y-6 text-[#1A201C] leading-relaxed">
        <p className="text-sm text-[#5A6B5D] italic">
          This Vendor Agreement (<strong>"Agreement"</strong>) is entered into as of the date of electronic acceptance or execution, by and between:
        </p>

        <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-5">
          <p className="text-sm">
            <strong className="text-[#1E3A2B]">AMIROOP HEALTHION LLP</strong>, a Limited Liability Partnership incorporated under the laws of India,
            represented by its designated partners/directors <strong>Dr. Rupali Srivastava</strong> and <strong>Amit Kumar Srivastava</strong>,
            operating the e-commerce platform <em>"Healthion India"</em> (hereinafter referred to as the <strong>"Company / Platform"</strong>,
            which expression shall include its successors and permitted assigns);
          </p>
          <p className="text-sm mt-3 font-mono uppercase text-center tracking-widest text-[#5A6B5D]">— AND —</p>
          <p className="text-sm mt-3">
            The <strong>Vendor / Brand Owner / Manufacturer</strong> (hereinafter referred to as the <strong>"Vendor"</strong>,
            which expression shall include its legal representatives, successors, and permitted assigns).
          </p>
        </div>

        <Clause n="1" title="Purpose & Scope of Services">
          <p>The Company operates an online marketplace under the brand name <strong>Healthion India</strong> dedicated to Ayurvedic, Herbal, Organic, and Holistic Wellness products.</p>
          <p>The Vendor desires to list, promote, and sell its legally manufactured and compliant wellness products through the Healthion India platform.</p>
        </Clause>

        <Clause n="2" title="Mandatory Compliance & Documentation">
          <p>Prior to listing any products, the Vendor must submit and maintain valid:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Goods and Services Tax Identification Number (GSTIN) and PAN.</li>
            <li>Certificate of Incorporation / Partnership Deed / Business Registration.</li>
            <li>Valid AYUSH Manufacturing/Marketing License or FSSAI License (as applicable to the product category).</li>
            <li>Complete Certificate of Analysis (COA) and batch lab reports upon request.</li>
          </ul>
        </Clause>

        <Clause n="3" title="Product Quality, Packaging & Expiry Standards">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Genuineness:</strong> The Vendor guarantees that all products are 100% authentic, unadulterated, accurately labeled, and compliant with the Drugs and Cosmetics Act, AYUSH guidelines, and FSSAI standards.</li>
            <li><strong>Shelf Life:</strong> No product with less than 6 months remaining shelf life (or less than 50% of total shelf life, whichever is higher) shall be dispatched to customers.</li>
            <li><strong>Packaging:</strong> The Vendor must package products securely in tamper-evident materials to withstand transit.</li>
          </ul>
        </Clause>

        <Clause n="4" title="Commercial Terms & Payment Settlement Cycle">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1E3A2B] text-[#FAF8F5]">
                  <th className="text-left p-3 font-serif font-semibold">Component</th>
                  <th className="text-left p-3 font-serif font-semibold">Policy / Term Details</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#E2DDD5]">
                  <td className="p-3 font-semibold">Marketplace Commission</td>
                  <td className="p-3"><strong>12%</strong> platform fee on the net selling price (exclusive of GST).</td>
                </tr>
                <tr className="border-b border-[#E2DDD5] bg-[#FAF8F5]">
                  <td className="p-3 font-semibold">Payment Gateway & Logistics</td>
                  <td className="p-3">Standard payment gateway transaction fees and shipping costs deducted as per actuals.</td>
                </tr>
                <tr className="border-b border-[#E2DDD5]">
                  <td className="p-3 font-semibold">Settlement Cycle</td>
                  <td className="p-3">Payouts processed every <strong>T+7 to T+10 business days</strong> after successful order delivery and expiry of the customer return window.</td>
                </tr>
                <tr className="bg-[#FAF8F5]">
                  <td className="p-3 font-semibold">Taxes & Invoicing</td>
                  <td className="p-3">Platform generates vendor payout statements; Vendor is responsible for remitting GST on product sales.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Clause>

        <Clause n="5" title="Order Fulfillment, Returns & Replacements">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Dispatch Timeline:</strong> The Vendor must process and dispatch orders within <strong>24 to 48 hours</strong> of receiving the order notification.</li>
            <li><strong>Returns & Damaged Goods:</strong> If a customer receives a damaged, expired, or incorrect product, the Vendor shall bear the full cost of replacement/refund and reverse logistics.</li>
          </ul>
        </Clause>

        <Clause n="6" title="Representations, Warranties & Indemnity">
          <p>The Vendor represents that its product formulations, packaging designs, and promotional materials do not infringe on any third-party intellectual property or make unsubstantiated medical claims (e.g., claiming to cure terminal illnesses without clinical backing).</p>
          <p>The Vendor agrees to indemnify and hold harmless <strong>Amiroop Healthion LLP</strong>, <strong>Dr. Rupali Srivastava</strong>, and <strong>Amit Kumar Srivastava</strong> from any customer claims, regulatory penalties, or legal actions arising out of product defects, mislabeling, or non-compliance with AYUSH/FSSAI norms.</p>
        </Clause>

        <Clause n="7" title="Term, Termination & Jurisdiction">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Term:</strong> This Agreement is effective from the date of onboarding and remains active until terminated by either party with a <strong>30-day written notice</strong>.</li>
            <li><strong>Immediate Termination:</strong> The Company reserves the right to immediately delist the Vendor for counterfeit goods, expired inventory, or regulatory violations.</li>
            <li><strong>Governing Law:</strong> This Agreement shall be governed by the laws of India, and any disputes shall be subject to the exclusive jurisdiction of the competent courts in <strong>Lucknow, Uttar Pradesh</strong>.</li>
          </ul>
        </Clause>

        <div className="border-t border-[#E2DDD5] pt-6">
          <div className="text-xs font-mono uppercase tracking-wider text-[#D97706]">Execution / Electronic Acceptance</div>

          <div className="grid sm:grid-cols-2 gap-6 mt-4">
            <div className="bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-5">
              <div className="text-xs font-mono uppercase text-[#1E3A2B]">For Amiroop Healthion LLP (Healthion India)</div>
              <div className="mt-3 space-y-2 text-sm">
                <SignatureLine label="Name" value="Dr. Rupali Srivastava / Amit Kumar Srivastava" />
                <SignatureLine label="Designation" value="Designated Partner / Authorized Signatory" />
                <SignatureLine label="Date" value="On onboarding acceptance" />
              </div>
            </div>
            <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-5">
              <div className="text-xs font-mono uppercase text-[#5A6B5D]">For the Vendor</div>
              <div className="mt-3 space-y-2 text-sm">
                <SignatureLine label="Business Name" value="__________________________" />
                <SignatureLine label="Authorized Signatory" value="__________________________" />
                <SignatureLine label="GSTIN / License No" value="__________________________" />
                <SignatureLine label="Date" value="__________________________" />
              </div>
            </div>
          </div>
        </div>
      </article>

      <div className="mt-6 sticky bottom-4 z-10">
        <div className="bg-white border-2 border-[#1E3A2B] rounded-2xl p-5 shadow-xl">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="agreement-accept-row">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-[#1E3A2B]"
              data-testid="agreement-accept-checkbox"
            />
            <span className="text-sm text-[#1A201C] flex-1">
              I have read, understood, and accept the <strong>Marketplace Vendor Agreement</strong> on behalf of my business, including compliance, commission, indemnity, and jurisdiction clauses.
            </span>
          </label>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            {!isVendorPreSignup && (
              <Link to="/vendor/register" className="btn-outline-forest text-sm">Skip for now</Link>
            )}
            <button
              disabled={!accepted}
              onClick={() => nav("/vendor/register", { state: { agreement_accepted: true } })}
              className={`text-sm inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-all ${accepted ? "bg-[#1E3A2B] text-white hover:bg-[#16291F]" : "bg-[#E2DDD5] text-[#5A6B5D] cursor-not-allowed"}`}
              data-testid="agreement-continue-button"
            >
              Accept & Continue to KYC →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Clause({ n, title, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl font-bold text-[#1E3A2B] mt-2 flex items-baseline gap-3">
        <span className="font-mono text-sm text-[#D97706]">{n}.</span> {title}
      </h2>
      <div className="mt-3 space-y-2 text-[15px]">{children}</div>
    </section>
  );
}

function SignatureLine({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-[#5A6B5D]">{label}</div>
      <div className="text-[#1A201C]">{value}</div>
    </div>
  );
}
